import React, { useContext, useEffect, useCallback, useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import { AppDispatch, RootState } from "../../../slices/store";
import { TodosContext } from "../../../context";
import {
  cleanPhoneNumberInput,
  validatePhoneNumber,
} from "../../../utils/validationUtils";

import {
  getSingleAccount,
  getSingleUser,
  getUserNotRequests,
  getUserRequests,
} from "../../../slices/users/userManagementSlice";

import {
  REQUESTED_USER_REQUEST_OPTIONS,
  USER_REQUEST_OPTIONS,
} from "./constants";

import useRequestPendingUser from "../../../hooks/useRequestPendingUsers";
import useRequestApprovedUsers from "../../../hooks/useRequestApprovedUsers";

import UseAuthConfig from "../../../hooks/useAuthConfig";
import BreadCrumb from "../../../components/organisms/Breadcrumb/BreadCrumb";
import Headline from "../../../components/organisms/Headline/Headline";
import LabelInsideInput from "../../../components/molecules/InputController/LabelInsideInputField";
import LabelInsideSelectField from "../../../components/molecules/InputController/LabelInsideSelectField";
import Loader from "../../../components/atoms/Loader/Loader";

import {
  buildCustomerPayload,
  buildUserPayload,
  UserDestination,
  userEditForm,
  userEditValidationSchema,
} from "../utils";

import { handleEditUser } from "../service/userService";
import { handleEditCustomer } from "../../client-management/service/clientService";

import { isEqual } from "lodash";

const UserEdit = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const { addAlert } = useContext(TodosContext);

  const [isChanged, setIsChanged] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const selectedUser = useSelector(
    (state: RootState) => state.userManagement.selectedUser
  );

  const [reqRequests] = useRequestPendingUser();
  const [usersRequests] = useRequestApprovedUsers();
  const { protectedResources }: any = UseAuthConfig();

  // Determine path (user or account)
  const pathParts = location.pathname.split("/");
  const isUser = pathParts[2] === "users";

  const endpoint = protectedResources.apiTodoList.endpoint;

  const { id } = useParams();

  /** ---------------------------------------
   * Handle mobile input change with sanitization
   * --------------------------------------*/
  const handleMobileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const cleaned = cleanPhoneNumberInput(e.target.value);

    // Count digits and limit to 15
    const digits = cleaned.replace(/\D/g, "");
    if (digits.length > 15) return;

    formik.setFieldValue("mobileNumber", cleaned);
  };

  /** ---------------------------------------
   * Validate on blur
   * --------------------------------------*/
  const handleMobileBlur = () => {
    formik.setFieldTouched("mobileNumber", true);
    const result = validatePhoneNumber(formik.values.mobileNumber);
    if (!result.isValid) {
      formik.setFieldError("mobileNumber", result.message);
    }
  };

  /** ---------------------------------------
   * Fetch selected user/account
   * --------------------------------------*/
  useEffect(() => {
    const parts = location.pathname.split("/"); // local variable
    const isUserPath = parts[2] === "users"; // local variable
    const id = parts[4]; // local variable

    if (parts.length === 5 && !selectedUser) {
      const accessToken = localStorage.getItem("AccessToken") || "";
      const url = `${endpoint}/${isUserPath ? "users" : "accounts"}/${id}`;

      dispatch(
        isUserPath
          ? getSingleUser({ accessToken, url })
          : getSingleAccount({ accessToken, url })
      );
    }
  }, [location.pathname, dispatch, endpoint, selectedUser]);

  /** ---------------------------------------
   * Build normalized original values
   * --------------------------------------*/
  const getOriginalValues = useCallback(() => {
    if (!selectedUser) return null;

    return {
      firstName: selectedUser.firstName ?? "",
      lastName: selectedUser.lastName ?? "",
      mobileNumber: selectedUser.mobileNumber ?? "",
      state: selectedUser.state
        ? { value: selectedUser.state, label: selectedUser.state }
        : null,
      jobTitle: selectedUser.jobTitle ?? "",
      note: selectedUser.note ?? "",
      email: selectedUser.email ?? "",
    };
  }, [selectedUser]);

  /** ---------------------------------------
   * Formik
   * --------------------------------------*/
  const formik = useFormik({
    initialValues: getOriginalValues() || userEditForm,
    enableReinitialize: true,
    validationSchema: userEditValidationSchema,
    onSubmit: (values) => {
      if (isEqual(values, getOriginalValues())) {
        addAlert("No changes detected", "warning", true);
        return;
      }

      setIsLoading(true);

      let url =
        endpoint +
        (isUser ? UserDestination.USERS : UserDestination.ACCOUNTS) +
        selectedUser?.id;

      if (isUser) {
        const payload = buildUserPayload(selectedUser!, values);
        handleEditUser(
          payload,
          dispatch,
          setIsLoading,
          addAlert,
          true,
          navigate,
          protectedResources,
          url,
          usersRequests,
          getUserNotRequests
        );
      } else {
        const payload = buildCustomerPayload(
          selectedUser!,
          values,
          Number(id) || 0
        );
        url = `${endpoint}/accounts/${id}`;
        handleEditCustomer(
          payload,
          dispatch,
          setIsLoading,
          addAlert,
          true,
          navigate,
          protectedResources,
          url,
          reqRequests,
          getUserRequests
        );
      }
    },
  });

  /** ---------------------------------------
   * Detect form changes
   * --------------------------------------*/
  useEffect(() => {
    if (!selectedUser) return;
    setIsChanged(!isEqual(formik.values, getOriginalValues()));
  }, [formik.values, selectedUser, getOriginalValues]);

  /** ---------------------------------------
   * UI Rendering
   * --------------------------------------*/
  return (
    <Container fluid className="layout-bar">
      <BreadCrumb />
      <Headline name="Edit User" />

      <div className="d-flex">
        <form onSubmit={formik.handleSubmit}>
          <div className="box-container">
            {/* Row 1 */}
            <Row>
              <Col xs={12} sm={6}>
                <LabelInsideInput
                  id="firstName"
                  label="First Name"
                  {...formik.getFieldProps("firstName")}
                  error={formik.errors.firstName}
                  touched={formik.touched.firstName}
                  showLabel={false}
                  disabled={!isUser}
                />
              </Col>

              <Col xs={12} sm={6}>
                <LabelInsideInput
                  id="lastName"
                  label="Last Name"
                  {...formik.getFieldProps("lastName")}
                  error={formik.errors.lastName}
                  touched={formik.touched.lastName}
                  showLabel={false}
                  disabled={!isUser}
                />
              </Col>
            </Row>

            {/* Row 2 */}
            <Row>
              <Col xs={12} sm={6}>
                <LabelInsideInput
                  id="email"
                  label="Email"
                  value={formik.values.email}
                  error={formik.errors.email}
                  touched={formik.touched.email}
                  showLabel={false}
                  disabled={true}
                />
              </Col>
              <Col xs={12} sm={6}>
                <LabelInsideInput
                  id="mobileNumber"
                  label="Mobile Number"
                  {...formik.getFieldProps("mobileNumber")}
                  onChange={handleMobileChange}
                  onBlur={handleMobileBlur}
                  error={formik.errors.mobileNumber}
                  touched={formik.touched.mobileNumber}
                  showLabel={false}
                  disabled={!isUser}
                />
              </Col>
            </Row>

            {/* Row 3 */}
            <Row>
              <Col xs={12} sm={6}>
                <LabelInsideSelectField
                  id="state"
                  name="state"
                  label={isUser ? "State" : "Status"}
                  value={formik.values.state}
                  options={
                    isUser
                      ? USER_REQUEST_OPTIONS
                      : REQUESTED_USER_REQUEST_OPTIONS
                  }
                  onChange={(v) => formik.setFieldValue("state", v)}
                  onBlur={() => formik.setFieldTouched("state", true)}
                  error={formik.errors}
                  touched={formik.touched}
                  width="w-100"
                  showLabel={false}
                />
              </Col>

              <Col xs={12} sm={6}>
                <LabelInsideInput
                  id="note"
                  label="Note"
                  {...formik.getFieldProps("note")}
                  error={formik.errors.note}
                  touched={formik.touched.note}
                  showLabel={false}
                />
              </Col>
            </Row>
            <Row>
              <Col xs={12} sm={6}>
                <LabelInsideInput
                  id="jobTitle"
                  label="Job Title"
                  {...formik.getFieldProps("jobTitle")}
                  error={formik.errors.jobTitle}
                  touched={formik.touched.jobTitle}
                  showLabel={false}
                  disabled={!isUser}
                />
              </Col>
              <Col xs={12} sm={6}></Col>
            </Row>
          </div>

          {/* Buttons */}
          <div className="footer-section box-container">
            <button
              className="btn btn-secondary"
              type="button"
              onClick={() => navigate(-1)}
            >
              Back
            </button>

            <button
              className="btn btn-primary"
              type="submit"
              disabled={!isChanged || isLoading}
              style={{
                opacity: !isChanged ? 0.6 : 1,
                cursor: !isChanged ? "not-allowed" : "pointer",
              }}
            >
              Submit
            </button>
          </div>
        </form>

        {isLoading && <Loader />}
      </div>
    </Container>
  );
};

export default UserEdit;
